import {
  useMyHrChangeRequestsEnabled,
  useMyHrUserRolesDataByContract,
} from "my-hr/hooks";
import { useMyNewsEnabled } from "my-news/hooks";
import {
  mainMenuChangeRequestsDisabled,
  mainMenuChangeRequestsEnabled,
  mainMenuHeaderGroup,
  mainMenuMyDocumentsCollapse,
  mainMenuMyDocumentsUploader,
  mainMenuMyDocumentsViewer,
  mainMenuSettingsMenu,
  myCafeteriaMenu,
  myLeaveMenu,
  myNewsMenu,
  myTimeCollapseMenu,
  myTimeMenu,
  myTimesheetMenu,
} from "oc/configs/sidebar-menu";
import { useOcCurrentUserState, useOcProperty } from "oc/hooks";
import { cloneMenuItemWithChildPath } from "oc/utils";

export const useOcGenerateMainMenu = () => {
  const menu = [mainMenuHeaderGroup];
  const { contractCode } = useOcCurrentUserState();
  const { data: rolesData = [] } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  const { hasRole } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  let allowedRoles = ["MyNews.user"];
  let notAllowedRoles: string[] = [];

  let hasMyNewsRight = hasRole(allowedRoles, notAllowedRoles);

  type Role = { product: string; role: string };

  const hasUploaderRole = rolesData.some(
    (role: Role) => role.product === "MyDocuments" && role.role === "uploader",
  );
  const hasViewerRole = rolesData.some(
    (role: Role) => role.product === "MyDocuments" && role.role === "viewer",
  );

  const { state: myNewsMenuEnabled } = useMyNewsEnabled();

  if (myNewsMenuEnabled && hasMyNewsRight) {
    // Create a new object instead of mutating the original
    menu.push({
      ...myNewsMenu,
      path: "/",
      type: "item",
      translation: myNewsMenu.translation ?? "",
      notAllowedRoles: myNewsMenu.notAllowedRoles ?? [],
    });
  }

  const { state: changeRequestsEnabled } = useMyHrChangeRequestsEnabled();

  const dynamicPath =
    myNewsMenuEnabled && hasMyNewsRight ? "/my-hr/my-info" : "/";

  if (changeRequestsEnabled) {
    // Clone the collapse menu and update the first child's path immutably
    const clonedMenu = cloneMenuItemWithChildPath(
      mainMenuChangeRequestsEnabled,
      0,
      dynamicPath,
    );
    menu.push({
      ...clonedMenu,
      translation: mainMenuChangeRequestsEnabled.translation ?? "",
      notAllowedRoles: mainMenuChangeRequestsEnabled.notAllowedRoles ?? [],
    });
  } else {
    // Create a new object instead of mutating the original
    menu.push({
      ...mainMenuChangeRequestsDisabled,
      path: dynamicPath,
      translation: mainMenuChangeRequestsDisabled.translation ?? "",
      notAllowedRoles: mainMenuChangeRequestsDisabled.notAllowedRoles ?? [],
    });
  }

  const { data: myDocumentsMenuEnabled } = useOcProperty({
    resource: "myDocuments",
    key: "myDocumentsMenuEnabled",
    fetchPolicy: "network-only",
  });
  const myDocumentsEnabled = myDocumentsMenuEnabled !== "false";

  let myDocumentsMenu = null;
  if (myDocumentsEnabled) {
    myDocumentsMenu =
      hasViewerRole && hasUploaderRole
        ? mainMenuMyDocumentsCollapse
        : hasViewerRole
          ? mainMenuMyDocumentsViewer
          : hasUploaderRole
            ? mainMenuMyDocumentsUploader
            : null;
  }

  const finalMenu = [
    ...menu,
    ...myLeaveMenu,
    myCafeteriaMenu,
    myTimeMenu,
    myTimeCollapseMenu,
    myTimesheetMenu,
    ...(myDocumentsMenu ? [myDocumentsMenu] : []),
    mainMenuSettingsMenu,
  ];

  return finalMenu;
};

export default useOcGenerateMainMenu;
